import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';

import { api } from '@api/client';
import { useRecentActions } from '@components/features/Overview/QuickLinks/hook';
import { CreateMerchantSchema } from '@components/features/Payments/Merchants/CreateMerchantDialog/schema';
import { UpdateMerchantSchema } from '@components/features/Payments/Merchants/schema';
import {
  ListMerchantsResponse,
  Merchant,
  MerchantCodesResponse,
} from '@components/features/Payments/Merchants/types';
import { isMerchantUuid } from '@components/features/Payments/Merchants/utils';
import { paymentsParams } from '@components/features/Payments/params';
import { useStrongParams } from '@utils/params';
import { tryCatch } from '@utils/tryCatch';

export const MERCHANTS_PAGE_SIZE = 50;

export const queryKeys = {
  paginatedMerchants: (
    appUuid: string,
    params: UsePaginatedMerchantsParams
  ) => [appUuid, 'merchants', 'page', params],
  merchant: (appUuid: string, merchantUuid: string) => [
    appUuid,
    'merchants',
    merchantUuid,
  ],
};

export const queryFilters = {
  paginatedMerchants: (appUuid: string) => ({
    queryKey: [appUuid, 'merchants', 'page'],
    exact: false,
  }),
  merchant: (appUuid: string, merchantUuid: string) => ({
    queryKey: queryKeys.merchant(appUuid, merchantUuid),
    exact: true,
  }),
};

export function useMerchantCodes() {
  const { appUuid } = useStrongParams(paymentsParams);
  return useQuery({
    queryKey: ['merchant-codes'],
    async queryFn() {
      const response =
        await api(appUuid).get<MerchantCodesResponse>('frontend/mcc');
      return response.json();
    },
  });
}

export interface UsePaginatedMerchantsParams {
  page?: number;
  pageSize?: number;
  q?: string;
}

export function usePaginatedMerchants(
  params?: UsePaginatedMerchantsParams,
  options?: UseQueryOptions<
    ListMerchantsResponse,
    unknown,
    ListMerchantsResponse,
    ReturnType<typeof queryKeys.paginatedMerchants>
  >
) {
  const queryClient = useQueryClient();
  const { appUuid } = useStrongParams(paymentsParams);
  const page = params?.page ?? 1;
  const pageSize = params?.pageSize ?? MERCHANTS_PAGE_SIZE;
  const q = params?.q?.trim() || undefined;

  return useQuery({
    queryKey: queryKeys.paginatedMerchants(appUuid, { page, pageSize, q }),
    async queryFn() {
      // If a merchant UUID is provided, fetch the merchant directly
      if (q && isMerchantUuid(q)) {
        const [, merchant] = await tryCatch(getMerchant(appUuid, q));
        return {
          data: merchant ? [merchant] : [],
          pageSize,
          nextPage: null,
          total: merchant ? 1 : 0,
        };
      }

      // Otherwise, fetch the merchants list
      const response = await api(appUuid).get<ListMerchantsResponse>(
        'payments/merchants',
        {
          searchParams: {
            q,
            page: page - 1,
            pageSize,
          },
        }
      );
      const data = await response.json();
      data.data.forEach((merchant) => {
        queryClient.setQueryData(
          queryKeys.merchant(appUuid, merchant.id),
          merchant
        );
      });
      return data;
    },
    keepPreviousData: true,
    ...options,
  });
}

async function getMerchant(appUuid: string, merchantUuid: string) {
  const response = await api(appUuid).get<Merchant>(
    `payments/merchants/${merchantUuid}`
  );
  return response.json();
}

export function useMerchant(
  merchantUuid: string,
  options?: UseQueryOptions<Merchant, unknown, Merchant, string[]>
) {
  const { appUuid } = useStrongParams(paymentsParams);
  return useQuery({
    queryKey: queryKeys.merchant(appUuid, merchantUuid),
    queryFn: () => getMerchant(appUuid, merchantUuid),
    ...options,
  });
}

export function useCreateMerchant() {
  const queryClient = useQueryClient();
  const { appUuid } = useStrongParams(paymentsParams);
  return useMutation({
    mutationFn: async (data: CreateMerchantSchema) => {
      const address: Record<string, string> = {
        line1: data.address.line1,
        city: data.address.city,
        postalCode: data.address.postalCode,
        country: data.address.country.toLocaleLowerCase(),
      };
      if (data.address.line2) {
        address.line2 = data.address.line2;
      }
      if (data.address.state) {
        address.state = data.address.state;
      }

      const response = await api(appUuid).post<Merchant>('payments/merchants', {
        json: {
          name: data.name,
          website: data.website,
          categoryCode: data.categoryCode,
          business: {
            legalName: data.legalName || data.name,
            address,
          },
        },
      });
      return response.json();
    },
    onSuccess(data) {
      queryClient.setQueryData(queryKeys.merchant(appUuid, data.id), data);
      void queryClient.invalidateQueries(
        queryFilters.merchant(appUuid, data.id)
      );
      void queryClient.invalidateQueries(
        queryFilters.paginatedMerchants(appUuid)
      );
    },
  });
}

async function patchMerchant(
  appUuid: string,
  merchantUuid: string,
  data: UpdateMerchantSchema
) {
  const response = await api(appUuid).patch<Merchant>(
    `payments/merchants/${merchantUuid}`,
    { json: data }
  );
  return response.json();
}

export function useUpdateMerchant(merchantUuid: string) {
  const queryClient = useQueryClient();
  const { appUuid } = useStrongParams(paymentsParams);
  return useMutation({
    mutationFn: async (data: UpdateMerchantSchema) => {
      const merchant = await patchMerchant(appUuid, merchantUuid, data);
      return merchant;
    },
    onSuccess(data) {
      queryClient.setQueryData(queryKeys.merchant(appUuid, merchantUuid), data);
      void queryClient.invalidateQueries(
        queryFilters.paginatedMerchants(appUuid)
      );
    },
  });
}

export function useRetryApplePayDomain(merchantUuid: string) {
  const queryClient = useQueryClient();
  const { appUuid } = useStrongParams(paymentsParams);
  return useMutation({
    mutationFn: async (domain: string) => {
      // Get the latest merchant data
      const merchant = await getMerchant(appUuid, merchantUuid);

      // Remove the domain from the merchant's current domains
      const domainSet = new Set(merchant.applePay.domains.map((d) => d.domain));
      domainSet.delete(domain);
      const updatedMerchant = await patchMerchant(appUuid, merchantUuid, {
        applePay: { domains: Array.from(domainSet) },
      });

      // Add the domain back to the merchant's domains
      const retryDomainSet = new Set(
        updatedMerchant.applePay.domains.map((d) => d.domain)
      );
      retryDomainSet.add(domain);
      const retriedMerchant = await patchMerchant(appUuid, merchantUuid, {
        applePay: { domains: Array.from(retryDomainSet) },
      });

      return retriedMerchant;
    },
    onSuccess(data) {
      queryClient.setQueryData(queryKeys.merchant(appUuid, merchantUuid), data);
      queryClient.invalidateQueries(
        queryFilters.merchant(appUuid, merchantUuid)
      );
      queryClient.invalidateQueries(queryFilters.paginatedMerchants(appUuid));
    },
  });
}

export function useDeleteMerchant(merchantUuid: string) {
  const queryClient = useQueryClient();
  const { appUuid } = useStrongParams(paymentsParams);
  const recents = useRecentActions();
  return useMutation({
    mutationFn: async () => {
      await api(appUuid).delete(`payments/merchants/${merchantUuid}`);
    },
    onSuccess() {
      for (const [
        key,
        page,
      ] of queryClient.getQueriesData<ListMerchantsResponse>(
        queryFilters.paginatedMerchants(appUuid)
      )) {
        if (!page) continue;
        queryClient.setQueryData(key, {
          ...page,
          data: page.data.filter((m) => m.id !== merchantUuid),
          total: page.total - 1,
        });
      }
      recents.remove({ type: 'merchant', id: merchantUuid });
      queryClient.removeQueries(queryFilters.merchant(appUuid, merchantUuid));
    },
    onSettled() {
      void queryClient.invalidateQueries(
        queryFilters.paginatedMerchants(appUuid)
      );
    },
  });
}
